"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import ResultCard from "@/components/ResultCard";
import { calcFence, type FenceResult } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";

const HEIGHT_OPTIONS = [
  { value: 4, label: "4 ft (2 rails)" },
  { value: 6, label: "6 ft (3 rails)" },
  { value: 8, label: "8 ft (3 rails)" },
];

const SPACING_OPTIONS = [
  { value: 8, label: "8 ft (standard)" },
  { value: 6, label: "6 ft (heavier soil)" },
];

export default function CalculatorClient() {
  const [totalLength, setTotalLength] = useState("");
  const [heightFt, setHeightFt] = useState<4 | 6 | 8>(6);
  const [postSpacing, setPostSpacing] = useState<6 | 8>(8);
  const [picketWidth, setPicketWidth] = useState("5.5");
  const [gapWidth, setGapWidth] = useState("0");
  const [numGates, setNumGates] = useState("0");
  const [result, setResult] = useState<FenceResult | null>(null);

  function handleCalculate() {
    const res = calcFence({
      totalLengthFt: parseFloat(totalLength) || 0,
      heightFt,
      postSpacingFt: postSpacing,
      picketWidthIn: parseFloat(picketWidth) || 5.5,
      gapBetweenPicketsIn: parseFloat(gapWidth) || 0,
      numGates: parseInt(numGates) || 0,
    });
    setResult(res);
  }

  return (
    <CalculatorShell
      name="Fence Calculator"
      description="Calculate the posts, rails, and pickets needed to build a wood privacy or picket fence."
      icon="fence"
      category="diy"
      accentColor="#84cc16"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Sloped terrain or custom layout adjustments may require extra posts or pickets. Verify local permit rules and post depth requirements before digging."
      proTip="Purchase 2–3 extra pickets for replacement due to future weathering, splitting, or warp."
      inputsPanel={
        <div className="space-y-4">
          <InputField id="fence-length" label="Total Fence Length" unit="ft" type="number" min="0" step="1" placeholder="100" value={totalLength} onChange={(e) => { setTotalLength(e.target.value); setResult(null); }} hint="Measure the full perimeter (or linear footage) of fence line" />
          <div className="grid grid-cols-2 gap-3">
            <SelectField id="fence-height" label="Fence Height" value={heightFt} onChange={(e) => { setHeightFt(parseInt(e.target.value) as 4 | 6 | 8); setResult(null); }} options={HEIGHT_OPTIONS} />
            <SelectField id="fence-spacing" label="Post Spacing" value={postSpacing} onChange={(e) => { setPostSpacing(parseInt(e.target.value) as 6 | 8); setResult(null); }} options={SPACING_OPTIONS} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <InputField id="fence-picket-width" label="Picket / Board Width" unit="in" type="number" min="0" step="0.25" placeholder="5.5" value={picketWidth} onChange={(e) => { setPicketWidth(e.target.value); setResult(null); }} hint='1×6 board = 5.5"' />
            <InputField id="fence-gap" label="Gap Between Pickets" unit="in" type="number" min="0" step="0.25" placeholder="0" value={gapWidth} onChange={(e) => { setGapWidth(e.target.value); setResult(null); }} hint="0 = privacy fence" />
          </div>
          <InputField id="fence-gates" label="Number of Gates" type="number" min="0" step="1" placeholder="0" value={numGates} onChange={(e) => { setNumGates(e.target.value); setResult(null); }} hint="Each gate adds 2 extra posts" />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Posts Needed" value={result.numPosts} unit="posts" highlight size="md" sub="Incl. gate posts" />
              <ResultCard label="Sections" value={result.numSections} unit="sections" accent />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Rails / Stringers" value={result.numRails} unit="rails" accent sub={`${result.numRails / result.numSections} rails/section`} />
              <ResultCard label="Concrete Bags" value={result.concreteBags} unit="bags (80 lb)" sub="~1 bag per post" />
            </div>
            <ResultCard label="Pickets / Boards (+ 10% buffer)" value={result.picketsWithWaste} unit="pickets" highlight size="lg" sub={`${result.picketsBase} pickets exact · +${result.picketsWithWaste - result.picketsBase} buffer`} />

            {/* Materials table */}
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Material Summary
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: "4×4 posts", value: `${result.numPosts}`, unit: "posts" },
                  { label: "2×4 rails", value: `${result.numRails}`, unit: "rails" },
                  { label: `1×6 pickets (${gapWidth}"gap)`, value: `${result.picketsWithWaste}`, unit: "pickets" },
                  { label: "80 lb concrete bags", value: `${result.concreteBags}`, unit: "bags" },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)]"
                  >
                    <span className="text-[12.5px] font-sans font-semibold text-[var(--ink-soft)]">{row.label}</span>
                    <div className="text-right font-mono text-[13px]">
                      <span className="text-[var(--ink)] font-bold">
                        {row.value}
                      </span>
                      <span className="text-[10px] text-[var(--ink-faint)] ml-1.5 lowercase">
                        {row.unit}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        {/* Row 1 - Left: Buying Guide */}
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Fence Construction Selection & Buying Guide
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            A durable fence relies on choosing <Link href="/articles/wood-vs-vinyl-vs-metal-fence-comparison/" className="text-[var(--accent)] hover:underline font-semibold">fencing materials</Link> suited to your environment. Cedar is highly valued for pickets due to its natural decay resistance (<strong>{CALCULATOR_SPECS.fence.buyingGuide.cedarDurabilityClass.value}</strong>) and minimal warping. However, for structural components in ground contact, pressure-treated lumber posts meeting <strong>{CALCULATOR_SPECS.fence.buyingGuide.treatedWoodStandard.value}</strong> standards are recommended. The horizontal rails (usually 2x4s) provide lateral strength; a 6-foot fence requires 3 rails to prevent the pickets from sagging or twisting over time.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Fence material should match exposure. Full-privacy panels catch more wind than spaced pickets, shaded wet lots accelerate rot, coastal sites can corrode fasteners, and large dogs or pool barriers may require stronger gates and latch hardware than a decorative garden fence.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            A useful material list includes posts, rails, pickets, gate frames, hinges, latches, caps, screws or nails rated for exterior use, concrete, gravel, string line, stakes, and finish. Gate openings often need heavier posts and diagonal bracing, so count them separately rather than treating them like standard fence sections.
          </p>
        </div>

        {/* Row 1 - Right: Buying Guide Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/fence_material_guide.webp" alt="Modern vertical wood privacy fence made of cedar pickets along a green yard" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        {/* Row 2 - Left: Site Prep Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/fence_site_prep.webp" alt="Fence line post holes dug in soil with layout string lines and Call 811 utility markings" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>

        {/* Row 2 - Right: Site Prep Guide */}
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Fence Construction Substrate Prep & Installation
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Before digging, you must check property lines and contact the national digging safety hotline by dialing <strong>{CALCULATOR_SPECS.fence.sitePrep.digSafetyLine.value}</strong> to locate underground utilities. Since post setting can disrupt boundary turf, check our guide on <Link href="/articles/cost-to-sod-or-reseed-lawn/" className="text-[var(--accent)] hover:underline font-semibold">lawn sodding and reseeding</Link> to repair any lawn damage. For structural stability against wind loads, follow the standard trade rule: set posts at a depth of at least <strong>{CALCULATOR_SPECS.fence.sitePrep.postDepthRule.value}</strong>, extending below your local frost line. Pour one 80 lb bag of concrete per post hole, anchoring the post plumb, and slope the concrete dome at the surface to shed rainwater away from the wood post.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Layout should start with the legal line, not the old fence. Verify survey pins, easements, utility corridors, HOA rules, pool barrier rules, and local height limits before ordering. A fence installed a few inches over the line can become a removal problem even when the material takeoff was perfect.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            On slopes, decide whether the fence will step or rack before counting pickets and rails. Stepped fences need more layout cuts and may leave triangular gaps; racked fences follow grade more smoothly but depend on material flexibility and rail design.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={CALCULATOR_SPECS.fence.industryStandards.windLoadCapacity.standardCode}
        sourceName={CALCULATOR_SPECS.fence.industryStandards.windLoadCapacity.sourceName}
        sourceUrl={CALCULATOR_SPECS.fence.industryStandards.windLoadCapacity.sourceUrl}
        paragraphs={[
          <p key="p1">
            Fences function as large sails in residential yards, capturing wind and translating substantial lateral loads directly to the supporting posts. Under the structural design parameters of <b>{CALCULATOR_SPECS.fence.industryStandards.windLoadCapacity.standardCode}</b>, fence assemblies in high-wind or coastal regions must be engineered to resist horizontal forces of up to <b>{CALCULATOR_SPECS.fence.industryStandards.windLoadCapacity.value} MPH</b> without collapsing or tilting. This resilience depends heavily on post material selection, spacing, and footing geometry.
          </p>,
          <p key="p2">
            To resist wind loading, fence posts should be set at a depth equal to one-third or one-half of their above-ground height. Footing holes should be at least three times the diameter of the post itself, filled with concrete, and domed slightly at the surface to encourage water runoff. In addition to structural specs, builders must respect municipal zoning guidelines. Most local ordinances limit front yard privacy fences to 4 feet in height and backyard fences to 6 feet, while enforcing strict setback clearances relative to property boundaries and public easements.
          </p>,
          <p key="p3">
            Utility marking is required before post holes are dug because fence lines commonly cross irrigation, gas, electric, telecom, lighting, and drainage runs. Public locate services may not mark private lines beyond the meter, so sprinkler, pool, landscape lighting, and detached-garage utilities may need separate locating.
          </p>,
          <p key="p4">
            Gate locations deserve early planning. A gate should align with walkways, trash routes, mower access, pool equipment, and emergency access while still landing on stable posts. Wide double gates may require larger posts, deeper footings, or metal frames to resist sagging.
          </p>
        ]}
        keyRequirements={[
          "Fence assemblies must resist lateral wind forces up to 115 MPH.",
          "Post hole depth should be one-third to one-half of the above-ground post height.",
          "Footing holes must be at least three times the diameter of the fence post.",
          "Respect local zoning heights (typically 4 feet in front yard, 6 feet in backyard).",
          "Call 811 and account for private utility lines before digging.",
          "Verify survey lines, easements, HOA rules, and pool barrier requirements.",
          "Count gates, corner posts, end posts, and slope adjustments separately."
        ]}
      />

      <FaqSection calculatorId="fence" />
      <RelatedCalculators calculatorId="fence" />
    </CalculatorShell>
  );
}
